"use client";

import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import CopyRow from "@/components/common/CopyRow";
import IntegrationConfirmDialog from "@/components/integrations/marketplace/IntegrationConfirmDialog";
import Icon from "@/components/library/Icon";
import { Button } from "@/components/ui/button";
import { JOTFORM_HIDDEN_FIELD_NAME } from "@/types/jotform";
import JotFormCallout from "./JotFormCallout";
import JotFormSection from "./JotFormSection";

interface JotFormSecretSectionProps {
  issued: boolean;
  updatedAt: string | null;
  revealedSecret: string | null;
  isIssuing: boolean;
  isDisconnecting: boolean;
  onIssue: () => Promise<boolean>;
  onDisconnect: () => Promise<boolean>;
  onDismissSecret: () => void;
  onChanged?: () => void;
}

/** `locale` is the i18n language, not the browser's — a Japanese UI said en-US. */
function formatChangedAt(updatedAt: string | null, locale: string): string {
  if (!updatedAt) return "";
  const parsed = new Date(updatedAt);
  return Number.isNaN(parsed.getTime()) ? updatedAt : parsed.toLocaleString(locale);
}

export default function JotFormSecretSection({
  issued,
  updatedAt,
  revealedSecret,
  isIssuing,
  isDisconnecting,
  onIssue,
  onDisconnect,
  onDismissSecret,
  onChanged,
}: Readonly<JotFormSecretSectionProps>) {
  const { t, i18n } = useTranslation("partners");
  const { t: ct } = useTranslation("common");
  const [rotateOpen, setRotateOpen] = useState(false);
  const [disconnectOpen, setDisconnectOpen] = useState(false);

  const handleIssue = useCallback(async () => {
    const succeeded = await onIssue();
    if (succeeded) onChanged?.();
  }, [onIssue, onChanged]);

  const handleRotate = useCallback(async () => {
    setRotateOpen(false);
    await handleIssue();
  }, [handleIssue]);

  const handleDisconnect = useCallback(async () => {
    setDisconnectOpen(false);
    const succeeded = await onDisconnect();
    if (succeeded) onChanged?.();
  }, [onDisconnect, onChanged]);

  return (
    <JotFormSection
      heading={t("jotformIntegration.secretHeading")}
      testId="jotform-secret-section"
    >
      {issued ? (
        <div className="flex flex-wrap items-center gap-2">
          <p className="flex-1 text-xs text-500">
            {t("jotformIntegration.lastChanged", {
              date: formatChangedAt(updatedAt, i18n.language),
            })}
          </p>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setRotateOpen(true)}
            disabled={isIssuing}
          >
            <Icon icon="arrows-rotate" />
            {t("jotformIntegration.rotateSecret")}
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setDisconnectOpen(true)}
            disabled={isDisconnecting}
          >
            {t("jotformIntegration.disconnect")}
          </Button>
        </div>
      ) : (
        <div className="space-y-2">
          <p className="text-xs text-500">{t("jotformIntegration.secretDescription")}</p>
          <Button type="button" size="sm" onClick={handleIssue} disabled={isIssuing}>
            {t("jotformIntegration.generateSecret")}
          </Button>
        </div>
      )}

      {revealedSecret && (
        <JotFormCallout
          tone="amber"
          message={t("jotformIntegration.secretShownOnce")}
          onDismiss={onDismissSecret}
          dismissLabel={ct("close")}
          testId="jotform-revealed-secret"
        >
          <CopyRow
            label={t("jotformIntegration.secretLabel")}
            value={revealedSecret}
            copiedMessage={t("jotformIntegration.secretCopied")}
            failedMessage={t("jotformIntegration.copyFailed")}
            copyLabel={t("jotformIntegration.copySecret")}
            testId="jotform-secret-copy-row"
          />
          <CopyRow
            label={t("jotformIntegration.hiddenFieldHeading")}
            value={JOTFORM_HIDDEN_FIELD_NAME}
            copiedMessage={t("jotformIntegration.hiddenFieldCopied")}
            failedMessage={t("jotformIntegration.copyFailed")}
            copyLabel={t("jotformIntegration.copyHiddenField")}
            testId="jotform-hidden-field-copy-row"
          />
          <p className="text-amber-700 dark:text-amber-300">
            {t("jotformIntegration.hiddenFieldDescription")}
          </p>
        </JotFormCallout>
      )}

      <IntegrationConfirmDialog
        open={rotateOpen}
        onOpenChange={setRotateOpen}
        title={t("jotformIntegration.rotateConfirmTitle")}
        body={t("jotformIntegration.rotateConfirmBody")}
        confirmLabel={t("jotformIntegration.rotateSecret")}
        cancelLabel={ct("cancel")}
        onConfirm={handleRotate}
        busy={isIssuing}
        testId="jotform-rotate-confirm"
      />

      <IntegrationConfirmDialog
        open={disconnectOpen}
        onOpenChange={setDisconnectOpen}
        title={t("jotformIntegration.disconnectConfirmTitle")}
        body={t("jotformIntegration.disconnectConfirmBody")}
        confirmLabel={t("jotformIntegration.disconnect")}
        cancelLabel={ct("cancel")}
        onConfirm={handleDisconnect}
        busy={isDisconnecting}
        testId="jotform-disconnect-confirm"
      />
    </JotFormSection>
  );
}
